import { Directive, inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';

import { ApWindowService } from '@ap/cdk';
import { ApContentService } from '@ap/content';
import { ApLoaderModalService } from '@ap/loader-modal';
import { ModalClosedReason } from '@ap/modal';

import { EXPIRED_CODE_ERROR_DETAIL, PairingCodeError, PairingCodeResult, RedirectUrls } from '../pairing-code/pairing-code.model';
import { PairingCodeResource } from '../pairing-code/pairing-code.resource';

import { content, Content } from './koppelcode-invoeren.content';
import { PairingCodeLength } from './koppelcode-invoeren.model';

@Directive()
export abstract class KoppelcodeInvoerenDirective implements OnInit {
    protected control: UntypedFormControl = new UntypedFormControl();
    protected pairingCodeIsInvalid = false;
    protected pairingCodeIsExpired = false;
    protected samenRekeningOpenenUrl: string;
    protected readonly content = Content;

    private loader: Subject<ModalClosedReason>;

    protected readonly contentService: ApContentService = inject(ApContentService);
    protected readonly loaderModalService: ApLoaderModalService = inject(ApLoaderModalService);
    protected readonly pairingCodeResource: PairingCodeResource = inject(PairingCodeResource);
    protected readonly windowService: ApWindowService = inject(ApWindowService);

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
    }

    public onClick(): void {
        const pairingCodeLength: PairingCodeLength = this.control.value.length;
        switch (pairingCodeLength) {
            case PairingCodeLength.PEGA_LEGACY:
                this.redirectToSamenRekeningOpenen(this.control.value);
                break;
            case PairingCodeLength.BSP:
                this.getPairingCodeResultFromBsp(this.control.value);
                break;
            default:
                this.pairingCodeIsInvalid = true;
        }
    }

    private redirectToSamenRekeningOpenen(activationCode: string): void {
        this.pairingCodeIsInvalid = false;
        this.windowService.location_href(`${this.samenRekeningOpenenUrl}?activationCode=${activationCode}`);
    }

    private getPairingCodeResultFromBsp(pairingCode: string): void {
        this.pairingCodeIsInvalid = false;
        this.pairingCodeIsExpired = false;
        this.loader = this.loaderModalService.openLoader('', '', 0, 0, 0);

        this.pairingCodeResource.getPairingCodeResultaat(pairingCode).subscribe({
            next: (result: PairingCodeResult): void => {
                const redirectUrl: string = this.getRedirectUrl(result.client_values);
                if (!redirectUrl) {
                    this.pairingCodeIsInvalid = true;
                    this.loader.next(ModalClosedReason.ACCEPTED);
                } else {
                    this.windowService.location_href(redirectUrl);
                }
            },
            error: (error: PairingCodeError): void => {
                if (error.error?.detail === EXPIRED_CODE_ERROR_DETAIL) {
                    this.pairingCodeIsExpired = true;
                }
                this.pairingCodeIsInvalid = true;
                this.loader.next(ModalClosedReason.ACCEPTED);
            }
        });
    }

    protected get pairingCodeInvalidMessage(): string {
        if (this.pairingCodeIsExpired) {
            return this.contentService.get(Content.EXPIRED_CODE);
        }
        return this.contentService.get(Content.INVALID_CODE);
    }

    protected abstract getRedirectUrl(redirectUrls: RedirectUrls): string;

    protected decodeUrl(url: string): string {
        if (url) {
            const doc = new DOMParser().parseFromString(url, 'text/html');
            return doc.documentElement.textContent;
        }
        return '';
    }
}
